import classNames from "classnames";
import { useAppSelector } from "hooks/redux";
import {
  IWebsiteBuilderTableHeadCell,
  TWebsiteBuilderTableCell,
  WEBSITE_BUILDER_TABLE_MAX_CELLS,
  WEBSITE_BUILDER_TABLE_MAX_ROWS,
  WEBSITE_BUILDER_TABLE_MIN_CELLS,
  websiteBuilderGenerateTableCellDesignStyles,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import { websiteBuilderFindIconAsset, WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";
import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { ConnectDragSource } from "react-dnd";
import { useTranslation } from "react-i18next";
import { useTableBuilderDomNodeRefMapRegistration } from "../hooks/useTableBuilderDomNodeRefMapRegistration";
import { useTableBuilderContext } from "../TableBuilderContext";
import { TableBuilderCellValueSettings } from "../ui/cellValueSettings/TableBuilderCellValueSettings";
import { TableBuilderOptionPlaceholder } from "../ui/option/TableBuilderOptionPlaceholder";

interface ITableBuilderCellViewProps {
  cell: TWebsiteBuilderTableCell;
  cellIndex: number;
  mobileContentCell: IWebsiteBuilderTableHeadCell;
  rowId: string;
  isDraggingRow: boolean;
  rowDrag?: ConnectDragSource;
  isDraggingColumn: boolean;
  columnDrag?: ConnectDragSource;
  dragColumnIndexRef: React.MutableRefObject<number>;
  headRowOptionAllowed: boolean;
}

type TTableBuilderCellViewUpdateTextValue = (textContent: string) => void;

const CELL_DRAG_OPACITY = 0;

export function TableBuilderCellView(props: ITableBuilderCellViewProps) {
  const {
    cell,
    cellIndex,
    mobileContentCell,
    rowId,
    isDraggingRow,
    rowDrag,
    isDraggingColumn,
    columnDrag,
    dragColumnIndexRef,
    headRowOptionAllowed,
  } = props;

  const { selection, dragLayerContext, tableNode, domNodeRefMap, operations } =
    useTableBuilderContext();

  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const domNodeRefMapRegistration = useTableBuilderDomNodeRefMapRegistration(cell.id, domNodeRefMap);

  const updateTextValueRef = useRef<TTableBuilderCellViewUpdateTextValue>(null);
  const textContentRef = useRef<HTMLDivElement>(null);

  const cellDesignStyles = websiteBuilderGenerateTableCellDesignStyles(cell);

  const mobileCellDesignStyles =
    mobileContentCell == null ? null : websiteBuilderGenerateTableCellDesignStyles(mobileContentCell);

  const textContent = cell.value.type === "text" ? cell.value.content : "";

  const CellTag = cell.type === "head" ? "th" : "td";

  const selected = selection?.type === "cell" && selection.id === cell.id;
  const columnSelected = selection?.type === "column" && selection.index === cellIndex;

  const tableNodeSettings = tableNode.container.settings;
  
  const rowCount = tableNodeSettings.body.rows.length;
  const cellCount = tableNodeSettings.head.row.cells.length;

  const draggingCell =
    (dragLayerContext.type === "column-active" && dragLayerContext.columnIndex === cellIndex) ||
    (dragLayerContext.type === "row-active" && dragLayerContext.bodyRow.id === rowId);

  function findCellIconAsset(targetCell: TWebsiteBuilderTableCell) {
    if (targetCell?.value.type !== "icon") {
      return null;
    }

    const { assetIdentity } = targetCell.value;

    return websiteBuilderFindIconAsset(iconAssets, assetIdentity);
  }

  const iconAsset = findCellIconAsset(cell);
  const mobileContentIconAsset = findCellIconAsset(mobileContentCell);

  useLayoutEffect(() => {
    return () => {
      if (
        selected &&
        updateTextValueRef.current != null &&
        textContentRef.current?.textContent != null
      ) {
        updateTextValueRef.current(textContentRef.current.textContent);
      }
    };
  }, [selected]);

  useEffect(() => {
    if (cell.value.type === "text") {
      updateTextValueRef.current = (textContent: string) => {
        operations.setCellValue(
          cell.id,
          {
            type: "text",
            content: textContent,
          },
        );
      };
    } else {
      updateTextValueRef.current = null;
    }
  }, [cell.value.type, operations.setCellValue, cell.id]);

  useEffect(() => {
    if (textContentRef.current != null) {
      textContentRef.current.textContent = textContent;
    }
  }, [textContent]);

  useEffect(() => {
    if (!selected || textContentRef.current === null) {
      return;
    }

    textContentRef.current.focus();

    const range = document.createRange();
    range.selectNodeContents(textContentRef.current);
    range.collapse(false);

    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [selected, cell.value.type]);

  function handlePaste(e: React.ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();

    const text = e.clipboardData.getData("text");

    const selection = window.getSelection();

    if (selection.rangeCount === 0) {
      return;
    }

    selection.deleteFromDocument();
    selection.getRangeAt(0).insertNode(document.createTextNode(text));
    selection.collapseToEnd();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      operations.setSelection(null);
    }
  }

  return (
    <CellTag
      ref={domNodeRefMapRegistration}
      className={classNames({
        "table-builder-cell-selected": selected,
        "table-builder-cell-column-selected": columnSelected,
      })}
      style={{
        opacity: draggingCell ? CELL_DRAG_OPACITY : null,
      }}
      onMouseEnter={() => operations.hoverColumn(cellIndex)}
      onMouseLeave={() => operations.blurColumn(cellIndex)}
      onClick={() => operations.setSelection({ type: "cell", id: cell.id })}
    >
      <>
        {headRowOptionAllowed && (
          <TableBuilderHeadRowOptionPlaceholder
            cellId={cell.id}
            rowId={rowId}
            rowCount={rowCount}
          />
        )}
        {rowDrag != null && (
          <TableBuilderBodyRowOptionPlaceholder
            cellId={cell.id}
            rowId={rowId}
            rowCount={rowCount}
            isDragging={isDraggingRow}
            rowDrag={rowDrag}
          />
        )}
        {columnDrag != null && (
          <TableBuilderColumnOptionPlaceholder
            cellId={cell.id}
            cellIndex={cellIndex}
            cellCount={cellCount}
            isDragging={isDraggingColumn}
            dragColumnIndexRef={dragColumnIndexRef}
            columnDrag={columnDrag}
          />
        )}
        {mobileContentCell != null && (
          <div className={cellDesignStyles.mobileContentContainerClassName}>
            {mobileContentCell.value.type === "icon" ? (
              <div
                className={classNames(mobileCellDesignStyles.contentClassName, mobileCellDesignStyles.iconClassName)}
                style={mobileCellDesignStyles.iconStyleProperties}
                dangerouslySetInnerHTML={{ __html: mobileContentIconAsset?.svg ?? (mobileContentCell.value.assetIdentity != null ? WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG : "") }}
              />
            ) : (
              <div className={mobileCellDesignStyles.contentClassName}>{mobileContentCell.value.content}</div>
            )}
          </div>
        )}
        {cell.value.type === "icon" ? (
          <div
            className={classNames(
              cellDesignStyles.contentClassName,
              cellDesignStyles.iconClassName,
              "table-builder-cell-icon",
            )}
            style={cellDesignStyles.iconStyleProperties}
            dangerouslySetInnerHTML={{ __html: iconAsset?.svg ?? (cell.value.assetIdentity != null ? WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG : "") }}
          />
        ) : (
          <div
            ref={textContentRef}
            className={classNames(
              cellDesignStyles.contentClassName,
              "table-builder-cell-text",
            )}
            contentEditable={selected}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
          />
        )}

        <TableBuilderCellValueSettings active={selected} cell={cell} />
      </>
    </CellTag>
  );
}

interface ITableBuilderHeadRowOptionPlaceholderProps {
  cellId: string;
  rowId: string;
  rowCount: number;
}

function TableBuilderHeadRowOptionPlaceholder(props: ITableBuilderHeadRowOptionPlaceholderProps) {
  const { cellId, rowId, rowCount } = props;

  const { t } = useTranslation();
  
  const { selection, operations } = useTableBuilderContext();

  const dropdownOpen =
    selection?.type === "row" &&
    selection.id === rowId &&
    selection.colorPickerType === "none";

  function toggleDropdown() {
    if (!dropdownOpen) {
      operations.setSelection({ type: "row", id: rowId, colorPickerType: "none" });
    } else {
      operations.setSelection(null);
    }
  }

  function clearSelection() {
    operations.setSelection(null);
  }

  return (
    <TableBuilderOptionPlaceholder
      type="head-row"
      cellId={cellId}
      isDragging={false}
      dropdownOpen={dropdownOpen}
      toggleDropdown={toggleDropdown}
      optionSections={[
        [
          {
            label: t("Insert Below"),
            disabled: rowCount >= WEBSITE_BUILDER_TABLE_MAX_ROWS,
            onClick: () => {
              operations.insertRowBelowHead();
              clearSelection();
            },
          },
          {
            label: t("Background Color"),
            onClick: () => {
              operations.setSelection({ type: "row", id: rowId, colorPickerType: "background-color" });
            },
          },
          {
            label: t("Text Color"),
            onClick: () => {
              operations.setSelection({ type: "row", id: rowId, colorPickerType: "color" });
            },
          },
          {
            label: t("Clear"),
            onClick: () => {
              operations.clearRow(rowId);
              clearSelection();
            },
          },
        ],
      ]}
    />
  );
}

interface ITableBuilderBodyRowOptionPlaceholderProps {
  cellId: string;
  rowId: string;
  rowCount: number;
  isDragging: boolean;
  rowDrag: ConnectDragSource;
}

function TableBuilderBodyRowOptionPlaceholder(props: ITableBuilderBodyRowOptionPlaceholderProps) {
  const { cellId, rowId, rowCount, isDragging, rowDrag } = props;

  const { t } = useTranslation();
  
  const { selection, operations } = useTableBuilderContext();

  const dropdownOpen =
    selection?.type === "row" &&
    selection.id === rowId &&
    selection.colorPickerType === "none";

  function toggleDropdown() {
    if (!dropdownOpen) {
      operations.setSelection({ type: "row", id: rowId, colorPickerType: "none" });
    } else {
      operations.setSelection(null);
    }
  }

  function clearSelection() {
    operations.setSelection(null);
  }
  
  return (
    <TableBuilderOptionPlaceholder
      type="body-row"
      cellId={cellId}
      isDragging={isDragging}
      dropdownOpen={dropdownOpen}
      toggleDropdown={toggleDropdown}
      optionSections={[
        [
          {
            label: t("Insert Above"),
            disabled: rowCount >= WEBSITE_BUILDER_TABLE_MAX_ROWS,
            onClick: () => {
              operations.insertRow(rowId, "above");
              clearSelection();
            },
          },
          {
            label: t("Insert Below"),
            disabled: rowCount >= WEBSITE_BUILDER_TABLE_MAX_ROWS,
            onClick: () => {
              operations.insertRow(rowId, "below");
              clearSelection();
            },
          },
        ],
        [
          {
            label: t("Background Color"),
            onClick: () => {
              operations.setSelection({ type: "row", id: rowId, colorPickerType: "background-color" });
            },
          },
          {
            label: t("Text Color"),
            onClick: () => {
              operations.setSelection({ type: "row", id: rowId, colorPickerType: "color" });
            },
          },
        ],
        [
          {
            label: t("Duplicate"),
            disabled: rowCount >= WEBSITE_BUILDER_TABLE_MAX_ROWS,
            onClick: () => {
              operations.duplicateRow(rowId);
              clearSelection();
            },
          },
          {
            label: t("Clear"),
            onClick: () => {
              operations.clearRow(rowId);
              clearSelection();
            },
          },
        ],
        [
          {
            label: t("Delete"),
            onClick: () => {
              operations.deleteRow(rowId);
              clearSelection();
            },
          },
        ],
      ]}
      dropdownRegistration={rowDrag}
    />
  );
}

interface ITableBuilderColumnOptionPlaceholderProps {
  cellId: string;
  cellIndex: number;
  cellCount: number;
  isDragging: boolean;
  dragColumnIndexRef: React.MutableRefObject<number>;
  columnDrag: ConnectDragSource;
}

function TableBuilderColumnOptionPlaceholder(props: ITableBuilderColumnOptionPlaceholderProps) {
  const { cellId, cellIndex, cellCount, isDragging, dragColumnIndexRef, columnDrag } = props;

  const { t } = useTranslation();
  
  const { selection, operations } = useTableBuilderContext();

  const dropdownOpen = selection?.type === "column" && selection.index === cellIndex;

  function toggleDropdown() {
    if (!dropdownOpen) {
      operations.setSelection({ type: "column", index: cellIndex });
    } else {
      operations.setSelection(null);
    }
  }

  function clearSelection() {
    operations.setSelection(null);
  }

  const dragColumnIndexRefRegistration = useCallback(
    (el: HTMLDivElement) => {
      if (el != null) {
        dragColumnIndexRef.current = cellIndex;
      } else {
        dragColumnIndexRef.current = null;
      }
    },
    [cellIndex],
  );

  return (
    <TableBuilderOptionPlaceholder
      type="column"
      cellId={cellId}
      isDragging={isDragging}
      dropdownOpen={dropdownOpen}
      toggleDropdown={toggleDropdown}
      optionSections={[
        [
          {
            label: t("Insert Before"),
            disabled: cellCount >= WEBSITE_BUILDER_TABLE_MAX_CELLS,
            onClick: () => {
              operations.insertColumn(cellIndex, "before");
              clearSelection();
            },
          },
          {
            label: t("Insert After"),
            disabled: cellCount >= WEBSITE_BUILDER_TABLE_MAX_CELLS,
            onClick: () => {
              operations.insertColumn(cellIndex, "after");
              clearSelection();
            },
          },
        ],
        [
          {
            label: t("Duplicate"),
            disabled: cellCount >= WEBSITE_BUILDER_TABLE_MAX_CELLS,
            onClick: () => {
              operations.duplicateColumn(cellIndex);
              clearSelection();
            },
          },
          {
            label: t("Clear"),
            onClick: () => {
              operations.clearColumn(cellIndex);
              clearSelection();
            },
          },
        ],
        [
          {
            label: t("Delete"),
            disabled: cellCount <= WEBSITE_BUILDER_TABLE_MIN_CELLS,
            onClick: () => {
              operations.deleteColumn(cellIndex);
              clearSelection();
            },
          },
        ],
      ]}
      dropdownRegistration={el => {
        columnDrag(el);
        dragColumnIndexRefRegistration(el);
      }}
    />
  );
}